import { Check, ChevronDown, ChevronRight, Copy } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  listAudit,
  listAuditActions,
  listUsers,
  type AuditEntry,
  type AuditQuery,
} from "@/api/admin-api";
import type { UserPublic } from "@/api/auth-api";

import { USERS_KEY } from "./UserTable";
import {
  zonedDateKey,
  zonedDayEndUtcIso,
  zonedDayStartUtcIso,
} from "@/utils/dateFormat";

import { auditTone, formatTimestamp } from "./lookups";

const AUDIT_KEY = ["admin", "audit"] as const;
const ACTIONS_KEY = ["admin", "audit", "actions"] as const;
const PAGE_SIZE = 50;
// One export pulls this many rows regardless of the page on screen — an
// export of "whatever page I happened to be looking at" is a trap. The
// server caps a page at 500, so this is four requests at most.
const EXPORT_MAX = 2000;

// Rows with no ``actor_id`` are not all the same thing, and a blanket
// "AGENT" on every one of them would be a lie in two directions.
//
// The on-prem agent really is the caller for these four — the two inbound
// pushes and the two dispatch acks it sends back after talking MLLP to
// Voyager.
const AGENT_ACTIONS = new Set([
  "AGENT_WORKLIST_PUSH",
  "AGENT_SR_PUSH",
  "REPORT_DISPATCHED_TO_RIS",
  "REPORT_DISPATCH_FAILED",
]);

// These run inside the web tier itself — the install-time bootstrap, the
// buffered-reading auto-merge, its cron sweep, and the test-inject route
// that simulates an inbound ORM. Calling any of them AGENT would credit
// the on-prem box with work it never did.
const SYSTEM_ACTIONS = new Set([
  "BOOTSTRAP",
  "ORM_INJECTED",
  "buffered_reading.auto_merged",
  "buffered_reading.sweep_merged",
]);

// A deployment is carried out by a person, but not by a person who has an
// account here — it is whoever ran ``update.sh`` on the box. Attributing
// it to a user row would mean inventing one, which would then appear in
// the actor filter and in user admin as though someone could log in as
// it. The row carries no actor and is labelled from the action instead.
const DEPLOY_ACTIONS = new Set(["DEPLOYMENT_APPLIED", "DEPLOYMENT_FAILED"]);

/** Label for an actor-less row, or null to keep the em-dash.
 *
 *  Null stays null on purpose for anything unrecognised. LOGIN_FAILED
 *  carries no actor when the username does not exist — that is a person
 *  we could not identify, and stamping a machine name on it would turn an
 *  unknown into a false attribution in exactly the log meant to be
 *  trustworthy. */
function nonHumanActor(action: string): string | null {
  if (AGENT_ACTIONS.has(action)) return "AGENT";
  if (SYSTEM_ACTIONS.has(action)) return "SYSTEM";
  if (DEPLOY_ACTIONS.has(action)) return "DEV TEAM";
  return null;
}

// Access-log actions are high-frequency ("opened worksheet", "opened
// images"). Rather than one table row per open, consecutive opens by the
// same user of the same target on the same day are folded into one
// collapsible row showing the count + last-open time; expanding lists
// each open.
const VIEW_ACTIONS = new Set(["WORKSHEET_VIEWED", "STUDY_IMAGES_VIEWED"]);

/** Calendar day key for grouping same-day opens, in the CLINIC's
 *  timezone. Grouping on the browser's clock would split a Brisbane
 *  working day for any viewer in another zone (and, against UTC, at
 *  10:00 local — mid-morning). */
function localDay(iso: string): string {
  return zonedDateKey(iso);
}

type DisplayItem =
  | { kind: "row"; row: AuditEntry }
  | { kind: "group"; key: string; rows: AuditEntry[] };

/** Fold consecutive same user + target + action + day view rows into one
 * group. Rows arrive newest-first, so a group's [0] is the latest open.
 * Non-view rows pass through untouched. (A group split across a page
 * boundary simply shows as two smaller groups — each count is still
 * exact for the rows on screen.) */
function buildDisplayItems(rows: AuditEntry[]): DisplayItem[] {
  const items: DisplayItem[] = [];
  for (const row of rows) {
    if (VIEW_ACTIONS.has(row.action)) {
      const key = `${row.actor_id ?? "?"}|${row.target_id ?? "?"}|${
        row.action
      }|${localDay(row.created_at)}`;
      const last = items[items.length - 1];
      if (last && last.kind === "group" && last.key === key) {
        last.rows.push(row);
        continue;
      }
      items.push({ kind: "group", key, rows: [row] });
    } else {
      items.push({ kind: "row", row });
    }
  }
  return items;
}

/** A picked date is a whole DAY. ``to`` widens to 23:59:59 local, or
 *  "1st to 1st" would be an empty range — the classic off-by-one that
 *  makes a filter look broken. */
function dayBounds(from: string, to: string): { since?: string; until?: string } {
  const out: { since?: string; until?: string } = {};
  // The picked dates mean CLINIC days; the API filters on UTC instants.
  // Converting via the browser clock would shift the window (10 h in
  // Brisbane) and silently drop the start of the range.
  if (from) out.since = zonedDayStartUtcIso(from);
  if (to) out.until = zonedDayEndUtcIso(to);
  return out;
}

export function AuditLogPanel() {
  const [actorFilter, setActorFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  // Typed accession is debounced into ``accession`` so the table does not
  // refetch on every keystroke of "26326-US".
  const [accessionInput, setAccessionInput] = useState<string>("");
  const [accession, setAccession] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setAccession(accessionInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [accessionInput]);

  // Any filter change resets to the first page — otherwise a filter that
  // yields fewer pages than the current one lands on an empty page.
  const onActorChange = (value: string) => {
    setActorFilter(value);
    setPage(1);
  };
  const onActionChange = (value: string) => {
    setActionFilter(value);
    setPage(1);
  };
  const onFromChange = (value: string) => {
    setFromDate(value);
    setPage(1);
  };
  const onToChange = (value: string) => {
    setToDate(value);
    setPage(1);
  };

  const usersQuery = useQuery({
    queryKey: USERS_KEY,
    queryFn: () => listUsers(true),
  });

  // The action list comes from the DB, not a hardcoded array: this panel
  // used to offer 9 of the ~40 actions the system emits, so template
  // changes and dispatches were invisible unless you picked "All".
  const actionsQuery = useQuery({
    queryKey: ACTIONS_KEY,
    queryFn: listAuditActions,
  });

  const filters: AuditQuery = useMemo(
    () => ({
      ...(actorFilter !== "all" ? { actor_id: actorFilter } : {}),
      ...(actionFilter !== "all" ? { action: actionFilter } : {}),
      ...(accession ? { accession } : {}),
      ...dayBounds(fromDate, toDate),
    }),
    [actorFilter, actionFilter, accession, fromDate, toDate],
  );

  const auditQuery = useQuery({
    queryKey: [...AUDIT_KEY, filters, page],
    queryFn: () => listAudit({ ...filters, page, page_size: PAGE_SIZE }),
    // Keep the current page on screen while the next one loads so
    // paging doesn't flash an empty table.
    placeholderData: (prev) => prev,
  });

  const onExport = async () => {
    setExporting(true);
    try {
      const collected: AuditEntry[] = [];
      for (let p = 1; collected.length < EXPORT_MAX; p += 1) {
        const chunk = await listAudit({ ...filters, page: p, page_size: 500 });
        collected.push(...chunk.items);
        if (chunk.items.length < 500 || collected.length >= chunk.total) break;
      }
      downloadTsv(collected, usersById);
    } finally {
      setExporting(false);
    }
  };

  const usersById = useMemo(() => {
    const m = new Map<string, UserPublic>();
    for (const u of usersQuery.data ?? []) m.set(u.id, u);
    return m;
  }, [usersQuery.data]);

  const rows = auditQuery.data?.items ?? [];
  const total = auditQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = (page - 1) * PAGE_SIZE + rows.length;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <section data-cy="audit-log-section" className="flex flex-col gap-4">
      <header className="flex items-end justify-between">
        <div>
          {/* Not "authentication" any more — it was named when logins were
              all it held. It now covers worksheet edits, study lifecycle,
              template changes, reports, dispatches and user admin. */}
          <h2 className="text-base font-semibold text-foreground">
            Activity audit log
          </h2>
          <p className="text-xs text-foreground-muted">
            Append-only event trail with hash-chained tamper detection.
            {total > 0
              ? ` Showing ${rangeStart}–${rangeEnd} of ${total} entries.`
              : ""}
          </p>
        </div>
      </header>

      <div
        className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background-card px-4 py-3"
        data-cy="audit-filters"
      >
        <label className="text-xs text-foreground-muted">
          User
          <select
            data-cy="filter-actor"
            value={actorFilter}
            onChange={(e) => onActorChange(e.target.value)}
            className="sk-input sk-sm ml-2 w-auto"
          >
            <option value="all">All users</option>
            {(usersQuery.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name ? `${u.full_name} (${u.username})` : u.username}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs text-foreground-muted">
          Action
          <select
            data-cy="filter-action"
            value={actionFilter}
            onChange={(e) => onActionChange(e.target.value)}
            className="sk-input sk-sm ml-2 w-auto"
          >
            <option value="all">All actions</option>
            {(actionsQuery.data ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs text-foreground-muted">
          Accession
          <input
            data-cy="filter-accession"
            value={accessionInput}
            onChange={(e) => setAccessionInput(e.target.value)}
            placeholder="26326-US"
            className="sk-input sk-sm ml-2 w-32"
          />
        </label>

        <label className="text-xs text-foreground-muted">
          From
          <input
            type="date"
            data-cy="filter-from"
            value={fromDate}
            onChange={(e) => onFromChange(e.target.value)}
            className="sk-input sk-sm ml-2 w-auto"
          />
        </label>

        <label className="text-xs text-foreground-muted">
          To
          <input
            type="date"
            data-cy="filter-to"
            value={toDate}
            onChange={(e) => onToChange(e.target.value)}
            className="sk-input sk-sm ml-2 w-auto"
          />
        </label>

        <button
          type="button"
          data-cy="audit-export"
          onClick={onExport}
          disabled={exporting || total === 0}
          className="rounded-md border border-accent bg-transparent px-3 py-1 text-xs font-semibold text-accent hover:bg-accent-light disabled:opacity-50"
          title={`Export the filtered log as TSV (up to ${EXPORT_MAX} rows).`}
        >
          {exporting ? "Exporting…" : "Export"}
        </button>

        <span className="ml-auto text-xs text-foreground-muted" data-cy="audit-row-count">
          {auditQuery.isLoading ? "Loading…" : `${total} total`}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-background-card shadow-theme-sm">
        <table className="w-full text-sm" data-cy="audit-table">
          <thead className="border-b-2 border-border-strong bg-table-header text-left text-[12px] font-semibold uppercase tracking-[0.05em] text-table-header-text">
            <tr>
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Detail</th>
              <th className="px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {auditQuery.isLoading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-10 text-center text-foreground-muted"
                  data-cy="audit-loading"
                >
                  Loading audit entries…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-10 text-center text-foreground-muted"
                  data-cy="audit-empty"
                >
                  No audit entries match these filters.
                </td>
              </tr>
            ) : (
              buildDisplayItems(rows).map((item) =>
                item.kind === "row" || item.rows.length === 1 ? (
                  (() => {
                    const row =
                      item.kind === "row" ? item.row : item.rows[0]!;
                    return (
                      <AuditRow
                        key={row.id}
                        row={row}
                        actor={
                          row.actor_id
                            ? usersById.get(row.actor_id) ?? null
                            : null
                        }
                      />
                    );
                  })()
                ) : (
                  <AuditViewGroup
                    key={`${item.key}-${item.rows[0]!.id}`}
                    rows={item.rows}
                    actor={
                      item.rows[0]!.actor_id
                        ? usersById.get(item.rows[0]!.actor_id!) ?? null
                        : null
                    }
                  />
                ),
              )
            )}
          </tbody>
        </table>
      </div>

      <div
        className="flex items-center justify-between gap-3"
        data-cy="audit-pager"
      >
        <span className="text-xs text-foreground-muted" data-cy="audit-page-label">
          Page {page} of {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-cy="audit-prev"
            disabled={!canPrev || auditQuery.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="sk-btn sk-sm"
          >
            Previous
          </button>
          <button
            type="button"
            data-cy="audit-next"
            disabled={!canNext || auditQuery.isFetching}
            onClick={() => setPage((p) => p + 1)}
            className="sk-btn sk-sm"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

function AuditRow({
  row,
  actor,
}: {
  row: AuditEntry;
  actor: UserPublic | null;
}) {
  const tone = auditTone(row.action);
  // A lookup rather than a nested ternary — it was already three deep at
  // three tones. ``deploy`` borrows the dicom badge's purple: a distinct
  // hue from the green / red / grey already in use, and an existing token
  // pair, so it needs no new CSS variable. Like ``badge-reception`` above
  // it, both halves of the pair are theme-independent, so the pairing
  // stays readable in dark — the trap that turned the status surfaces
  // white-on-white was pairing a pale background with ``--foreground``.
  const TONE_CLASS: Record<typeof tone, string> = {
    success: "bg-status-done-bg text-status-done",
    danger: "bg-status-urgent-bg text-status-urgent",
    deploy: "bg-badge-dicom-bg text-badge-dicom",
    neutral: "bg-badge-reception-bg text-badge-reception",
  };
  const toneClass = TONE_CLASS[tone];

  return (
    <tr
      data-cy="audit-row"
      data-action={row.action}
      className="border-t border-border"
    >
      <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] text-foreground-muted">
        {formatTimestamp(row.created_at)}
      </td>
      <td className="px-4 py-2.5">
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${toneClass}`}
        >
          {row.action}
        </span>
      </td>
      <td className="px-4 py-2.5 text-xs">
        {actor ? (
          <>
            <span className="text-foreground">{actor.full_name ?? actor.username}</span>{" "}
            <span className="font-mono text-[10px] text-foreground-muted">
              {actor.username}
            </span>
          </>
        ) : row.actor_id ? (
          <span className="font-mono text-[11px] text-foreground-muted">
            {row.actor_id.slice(0, 8)}…
          </span>
        ) : nonHumanActor(row.action) ? (
          <span
            className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] font-semibold text-foreground-muted"
            data-cy="actor-non-human"
          >
            {nonHumanActor(row.action)}
          </span>
        ) : (
          <span className="text-foreground-muted">—</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-xs text-foreground">
        <DetailCell metadata={row.metadata_json} targetId={row.target_id} />
      </td>
      <td className="px-4 py-2.5 font-mono text-[11px] text-foreground-muted">
        {row.ip_address ?? "—"}
      </td>
    </tr>
  );
}

function AuditViewGroup({
  rows,
  actor,
}: {
  rows: AuditEntry[];
  actor: UserPublic | null;
}) {
  const [open, setOpen] = useState(false);
  const latest = rows[0]!; // newest-first → the last open
  const tone = auditTone(latest.action);
  const toneClass =
    tone === "success"
      ? "bg-status-done-bg text-status-done"
      : tone === "danger"
        ? "bg-status-urgent-bg text-status-urgent"
        : "bg-badge-reception-bg text-badge-reception";
  const accession =
    typeof latest.metadata_json?.accession === "string"
      ? (latest.metadata_json.accession as string)
      : null;

  return (
    <>
      <tr
        data-cy="audit-view-group"
        data-action={latest.action}
        className="cursor-pointer border-t border-border hover:bg-table-row-hover"
        onClick={() => setOpen((o) => !o)}
      >
        <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] text-foreground-muted">
          {formatTimestamp(latest.created_at)}
        </td>
        <td className="px-4 py-2.5">
          <span className="inline-flex items-center gap-1">
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${toneClass}`}
            >
              {latest.action}
            </span>
            <span
              data-cy="audit-view-count"
              className="rounded-full bg-background-subtle px-1.5 py-0.5 text-[10px] font-semibold text-foreground-muted"
            >
              ×{rows.length}
            </span>
            {open ? (
              <ChevronDown className="h-3.5 w-3.5 text-foreground-muted" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-foreground-muted" />
            )}
          </span>
        </td>
        <td className="px-4 py-2.5 text-xs">
          {actor ? (
            <>
              <span className="text-foreground">
                {actor.full_name ?? actor.username}
              </span>{" "}
              <span className="font-mono text-[10px] text-foreground-muted">
                {actor.username}
              </span>
            </>
          ) : (
            <span className="text-foreground-muted">—</span>
          )}
        </td>
        <td className="px-4 py-2.5 text-xs text-foreground-muted">
          {accession ? (
            <span>
              accession=
              <span className="font-mono text-foreground">{accession}</span>{" "}
            </span>
          ) : null}
          <span>
            {rows.length} open{rows.length === 1 ? "" : "s"} this day — click to
            expand
          </span>
        </td>
        <td className="px-4 py-2.5 font-mono text-[11px] text-foreground-muted">
          {latest.ip_address ?? "—"}
        </td>
      </tr>
      {open ? (
        <tr
          data-cy="audit-view-group-detail"
          className="border-t border-border bg-background-subtle"
        >
          <td colSpan={5} className="px-4 py-2">
            <ul className="flex flex-col gap-1">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className="flex gap-6 font-mono text-[11px] text-foreground-muted"
                >
                  <span className="text-foreground">
                    {formatTimestamp(r.created_at)}
                  </span>
                  <span>{r.ip_address ?? "—"}</span>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function DetailCell({
  metadata,
  targetId,
}: {
  metadata: Record<string, unknown>;
  targetId: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const entries = Object.entries(metadata ?? {});
  if (entries.length === 0 && !targetId) {
    return <span className="text-foreground-muted">—</span>;
  }

  // The table truncates target_id and stringifies values, so the full
  // detail isn't readable on screen. Copy the untruncated payload.
  const fullDetail = JSON.stringify(
    { ...(targetId ? { target_id: targetId } : {}), ...(metadata ?? {}) },
    null,
    2,
  );
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullDetail);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked (insecure context / permissions) — the
      // detail is still on screen, so silently do nothing.
    }
  };

  return (
    <div className="flex items-start gap-2">
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {targetId ? (
          <span className="text-[11px] text-foreground-muted">
            target=<span className="font-mono text-foreground">{targetId.slice(0, 8)}…</span>
          </span>
        ) : null}
        {entries.map(([k, v]) => (
          <span key={k} className="text-[11px] text-foreground-muted">
            {k}=
            <span className="font-mono text-foreground">{stringify(v)}</span>
          </span>
        ))}
      </div>
      <button
        type="button"
        data-cy="audit-detail-copy"
        aria-label="Copy full detail"
        title={copied ? "Copied" : "Copy full detail"}
        onClick={onCopy}
        className="mt-0.5 shrink-0 rounded p-0.5 text-foreground-muted hover:bg-table-row-hover hover:text-foreground"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  // A deployment's commit list is [{label?, description}]. Left to
  // JSON.stringify it renders as a wall of braces and quotes in a cell
  // meant to be read at a glance; the copy button still yields the exact
  // payload. Any other array/object keeps the JSON fallback.
  if (Array.isArray(v) && v.length > 0 && isCommitList(v)) {
    return v
      .map((c) => (c.label ? `${c.label}: ${c.description}` : c.description))
      .join(" · ");
  }
  return JSON.stringify(v);
}

interface CommitEntry {
  label?: string;
  description: string;
}

function isCommitList(v: unknown[]): v is CommitEntry[] {
  return v.every(
    (e) =>
      typeof e === "object" &&
      e !== null &&
      typeof (e as CommitEntry).description === "string",
  );
}


// ---------------------------------------------------------------------
// TSV export — same shape as the measurement-gaps export so the two
// downloads open the same way.
// ---------------------------------------------------------------------

function tsvCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  const text = typeof v === "string" ? v : JSON.stringify(v);
  // Tabs and newlines would break the row/column structure.
  return text.replace(/[\t\r\n]+/g, " ");
}

function downloadTsv(
  rows: AuditEntry[],
  usersById: Map<string, UserPublic>,
): void {
  const header = [
    "timestamp",
    "action",
    "user",
    "username",
    "accession",
    "target_id",
    "ip",
    "detail",
  ];
  const lines = [header.join("\t")];
  for (const r of rows) {
    const u = r.actor_id ? usersById.get(r.actor_id) : undefined;
    const meta = (r.metadata_json ?? {}) as Record<string, unknown>;
    lines.push(
      [
        r.created_at,
        r.action,
        // Same label the table shows, so a spreadsheet and the screen
        // never disagree about who did something.
        u?.full_name ?? nonHumanActor(r.action) ?? "",
        u?.username ?? r.actor_id ?? nonHumanActor(r.action) ?? "",
        meta.accession ?? "",
        r.target_id ?? "",
        r.ip_address ?? "",
        JSON.stringify(meta),
      ]
        .map(tsvCell)
        .join("\t"),
    );
  }
  const blob = new Blob([lines.join("\n")], {
    type: "text/tab-separated-values;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.tsv`;
  a.click();
  URL.revokeObjectURL(url);
}
